import { z } from 'zod';
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import {
  getMissingRequiredFields,
  type MissingRequiredField,
} from '../pages/appointments/utils/questionnaireResponseRequired';
import { clientPortalSignatureSchema } from '../schemas/questionnaire-response.schema';
import { validateTemplateSpecificFormFields } from './templateFormValidation';
import { isSignatureDateQuestion } from '@unify/fhir-model/signature-date';

export { clientPortalSignatureSchema };

type RequiredFieldSpec = { linkId: string; label: string };

function collectRequiredFieldsFromQuestionnaire(
  items: QuestionnaireItem[] | undefined,
  out: RequiredFieldSpec[],
): void {
  for (const item of items ?? []) {
    if (item.type === 'group') {
      if (item.required && item.linkId) {
        out.push({ linkId: item.linkId, label: item.text?.trim() || item.linkId });
      }
      collectRequiredFieldsFromQuestionnaire(item.item, out);
      continue;
    }
    if (item.type === 'display') continue;
    if (item.type === 'date' && isSignatureDateQuestion(item)) continue;
    if (item.required && item.linkId) {
      out.push({ linkId: item.linkId, label: item.text?.trim() || item.linkId });
    }
    if (item.item?.length) {
      collectRequiredFieldsFromQuestionnaire(item.item, out);
    }
  }
}

export function isAnswerProvided(
  answers: QuestionnaireResponseItemAnswer[] | undefined,
): boolean {
  if (!answers?.length) return false;
  return answers.some((answer) => {
    if (answer.valueString !== undefined) return answer.valueString.trim().length > 0;
    if (answer.valueBoolean !== undefined) return true;
    if (answer.valueInteger !== undefined) return true;
    if (answer.valueDecimal !== undefined) return true;
    if (answer.valueDate !== undefined) return answer.valueDate.length > 0;
    if (answer.valueDateTime !== undefined) return answer.valueDateTime.length > 0;
    if (answer.valueTime !== undefined) return answer.valueTime.length > 0;
    if (answer.valueUri !== undefined) return answer.valueUri.length > 0;
    if (answer.valueCoding) return true;
    if (answer.valueQuantity?.value !== undefined) return true;
    if (answer.valueReference?.reference) return true;
    if (answer.valueAttachment?.url || answer.valueAttachment?.data) return true;
    return false;
  });
}

/** Collect every linkId under a questionnaire item subtree (for page-scoped validation). */
export function collectQuestionnaireItemLinkIds(
  items: QuestionnaireItem[] | undefined,
): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: QuestionnaireItem[] | undefined): void => {
    for (const item of nodes ?? []) {
      if (item.linkId) ids.add(item.linkId);
      walk(item.item);
    }
  };
  walk(items);
  return ids;
}

function missingFieldInScope(
  field: MissingRequiredField,
  scopeLinkIds: Set<string>,
): boolean {
  return field.itemPath.split('/').some((segment) => scopeLinkIds.has(segment));
}

function toQuestionnaireResponse(
  response: Pick<QuestionnaireResponse, 'item'>,
): QuestionnaireResponse {
  return {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    ...response,
  };
}

function toValidationResult(
  missing: MissingRequiredField[],
): QuestionnaireFillValidationResult {
  if (missing.length === 0) {
    return { success: true };
  }
  const first = missing[0];
  const message =
    missing.length === 1
      ? `${first.label} is required`
      : `Please complete all required fields (${missing.length} remaining).`;
  const missingLinkIds = [
    ...new Set(
      missing
        .map((field) => field.itemPath.split('/').pop())
        .filter((linkId): linkId is string => Boolean(linkId)),
    ),
  ];
  return {
    success: false,
    message,
    missingLinkIds,
    missingItemPath: first.itemPath,
  };
}

export type QuestionnaireFillValidationResult =
  | { success: true }
  | {
      success: false;
      message: string;
      missingLinkIds: string[];
      /** First missing field path — matches PreviewItemArray `data-item-path`. */
      missingItemPath: string;
    };

export type ValidateRequiredTemplateFieldsOptions = {
  skipLinkIds?: ReadonlySet<string>;
};

/**
 * Validate that all required Questionnaire items have answers in the response.
 * Uses nested response walking and respects `enableWhen` (via Medplum isQuestionEnabled).
 */
export function validateRequiredTemplateFields(
  questionnaire: Questionnaire,
  response: Pick<QuestionnaireResponse, 'item'>,
  options?: ValidateRequiredTemplateFieldsOptions,
): QuestionnaireFillValidationResult {
  const missing = getMissingRequiredFields(
    questionnaire,
    toQuestionnaireResponse(response),
    options,
  );
  const requiredResult = toValidationResult(missing);
  if (!requiredResult.success) return requiredResult;

  return validateTemplateSpecificFormFields(questionnaire, response);
}

/**
 * Validate required fields that belong to the current questionnaire page / item scope
 * (e.g. before advancing paginated assessment steps).
 */
export function validateRequiredTemplateFieldsInScope(
  questionnaire: Questionnaire,
  response: Pick<QuestionnaireResponse, 'item'>,
  scopeItems: QuestionnaireItem[] | undefined,
  options?: ValidateRequiredTemplateFieldsOptions,
): QuestionnaireFillValidationResult {
  const scopeLinkIds = collectQuestionnaireItemLinkIds(scopeItems);
  if (scopeLinkIds.size === 0) {
    return { success: true };
  }
  const missing = getMissingRequiredFields(
    questionnaire,
    toQuestionnaireResponse(response),
    options,
  ).filter((field) => missingFieldInScope(field, scopeLinkIds));
  const requiredResult = toValidationResult(missing);
  if (!requiredResult.success) return requiredResult;

  return validateTemplateSpecificFormFields(questionnaire, response, { scopeLinkIds });
}

/**
 * Build a Zod object schema for required template fields (by linkId).
 * Provider-locked fields can be omitted via `skipLinkIds`.
 */
export function buildRequiredTemplateFieldsSchema(
  questionnaire: Questionnaire,
  skipLinkIds?: ReadonlySet<string>,
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const required: RequiredFieldSpec[] = [];
  collectRequiredFieldsFromQuestionnaire(questionnaire.item, required);
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const { linkId, label } of required) {
    if (skipLinkIds?.has(linkId)) continue;
    shape[linkId] = z.custom<QuestionnaireResponseItemAnswer[] | undefined>(
      (val: unknown) => isAnswerProvided(val as QuestionnaireResponseItemAnswer[] | undefined),
      { message: `${label} is required` },
    );
  }
  return z.object(shape);
}

export function isClientPortalSignatureValid(signature: unknown): boolean {
  return clientPortalSignatureSchema.safeParse(signature).success;
}
